import React, { useEffect, useState } from 'react'
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp
} from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import AdminLayout from './AdminLayout'

const ALL_PERMISSIONS = [
  { key: 'admin:super',  label: 'Супер-адмін',         desc: 'Повний доступ до всіх розділів' },
  { key: 'admin:pages',  label: 'Управління новинами',  desc: 'Створення, редагування, видалення новин' },
  { key: 'admin:users',  label: 'Управління користувачами', desc: 'Запрошення та керування акаунтами' },
  { key: 'admin:roles',  label: 'Управління ролями',    desc: 'Налаштування прав і ролей' },
]

const emptyForm = { name: '', permissions: [] }

const AdminRoles = () => {
  const [roles, setRoles] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(null)

  useEffect(() => {
    return onSnapshot(collection(db, 'roles'), snap => {
      setRoles(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    })
  }, [])

  const togglePerm = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key],
    }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setSaving(true)
    try {
      if (editingId) {
        await updateDoc(doc(db, 'roles', editingId), {
          name: form.name.trim(), permissions: form.permissions,
        })
      } else {
        await addDoc(collection(db, 'roles'), {
          name: form.name.trim(), permissions: form.permissions, createdAt: serverTimestamp(),
        })
      }
      setForm(emptyForm); setEditingId(null)
    } finally { setSaving(false) }
  }

  const handleEdit = (role) => {
    setEditingId(role.id)
    setForm({ name: role.name, permissions: role.permissions || [] })
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити цю роль?')) return
    setDeleting(id)
    await deleteDoc(doc(db, 'roles', id))
    setDeleting(null)
  }

  return (
    <AdminLayout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <h1 style={s.pageTitle}>Ролі</h1>
          <p style={s.pageDesc}>Налаштуйте ролі та дозволи для адміністраторів</p>
        </div>

        <div style={s.cols}>
          {/* Form card */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>🔑</span>
              <span style={s.cardTitle}>{editingId ? 'Редагувати роль' : 'Нова роль'}</span>
            </div>

            <form onSubmit={handleSave} style={s.form}>
              <div style={s.field}>
                <label style={s.label}>Назва ролі</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={s.input}
                  placeholder='Наприклад: Редактор'
                  required
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Дозволи</label>
                <div style={s.permsList}>
                  {ALL_PERMISSIONS.map(p => {
                    const checked = form.permissions.includes(p.key)
                    return (
                      <label key={p.key} style={{ ...s.permRow, ...(checked ? s.permRowChecked : {}) }}>
                        <div style={{ ...s.checkbox, ...(checked ? s.checkboxChecked : {}) }}>
                          {checked && <span style={s.checkmark}>✓</span>}
                        </div>
                        <input
                          type='checkbox'
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          style={{ display: 'none' }}
                        />
                        <div>
                          <div style={s.permLabel}>{p.label}</div>
                          <div style={s.permDesc}>{p.desc}</div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div style={s.btnRow}>
                <button type='submit' disabled={saving} style={s.btnPrimary}>
                  {saving ? 'Збереження…' : editingId ? 'Зберегти зміни' : 'Створити роль'}
                </button>
                {editingId && (
                  <button type='button' onClick={() => { setForm(emptyForm); setEditingId(null) }} style={s.btnGhost}>
                    Скасувати
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* List card */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardIcon}>📋</span>
              <span style={s.cardTitle}>Всі ролі</span>
              <span style={s.badge}>{roles.length}</span>
            </div>

            {roles.length === 0 && (
              <div style={s.empty}>Ролей ще немає. Створіть першу роль.</div>
            )}

            <div style={s.roleList}>
              {roles.map(role => (
                <div key={role.id} style={s.roleCard}>
                  <div style={s.roleTop}>
                    <span style={s.roleName}>{role.name}</span>
                    <div style={s.roleActions}>
                      <button onClick={() => handleEdit(role)} style={s.iconBtn} title='Редагувати'>✏️</button>
                      <button
                        onClick={() => handleDelete(role.id)}
                        disabled={deleting === role.id}
                        style={s.iconBtn}
                        title='Видалити'
                      >🗑</button>
                    </div>
                  </div>
                  <div style={s.permTags}>
                    {(role.permissions || []).length === 0
                      ? <span style={s.noPerm}>Без дозволів</span>
                      : (role.permissions || []).map(p => {
                          const found = ALL_PERMISSIONS.find(ap => ap.key === p)
                          return <span key={p} style={s.permTag}>{found?.label || p}</span>
                        })
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

const s = {
  page: { maxWidth: '1000px' },
  pageHeader: { marginBottom: '28px' },
  pageTitle: { margin: '0 0 6px', fontSize: '24px', fontWeight: '700', color: '#2d1f14' },
  pageDesc: { margin: 0, fontSize: '14px', color: '#9a7c63' },

  cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' },

  card: {
    background: '#fff',
    borderRadius: '14px',
    padding: '28px',
    boxShadow: '0 2px 16px rgba(92,61,46,0.07)',
    border: '1px solid #f0e4d4',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '22px',
    paddingBottom: '16px',
    borderBottom: '1px solid #f5ede3',
  },
  cardIcon: { fontSize: '18px' },
  cardTitle: { fontSize: '15px', fontWeight: '700', color: '#2d1f14', flex: 1 },
  badge: {
    background: '#f0e6d8',
    color: '#8b6044',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },

  field: { marginBottom: '20px' },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#8b6044',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#2d1f14',
    background: '#fdfaf7',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
  },

  permsList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  permRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1.5px solid #f0e4d4',
    cursor: 'pointer',
    background: '#fdfaf7',
    transition: 'all 0.15s',
  },
  permRowChecked: {
    background: '#fdf5ec',
    borderColor: '#c4986a',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    borderRadius: '5px',
    border: '2px solid #d4b896',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '1px',
    transition: 'all 0.15s',
  },
  checkboxChecked: {
    background: '#c4986a',
    borderColor: '#c4986a',
  },
  checkmark: {
    fontSize: '11px',
    color: '#fff',
    fontWeight: '700',
    lineHeight: 1,
  },
  permLabel: { fontSize: '13px', fontWeight: '600', color: '#2d1f14', marginBottom: '2px' },
  permDesc: { fontSize: '11px', color: '#9a7c63' },

  form: {
    width: '100%', margin: 0, marginTop: 0, padding: 0,
    border: 'none', borderRadius: 0, background: 'none', boxShadow: 'none', display: 'block',
  },
  btnRow: { display: 'flex', gap: '10px', marginTop: '24px' },
  btnPrimary: {
    padding: '11px 24px',
    background: 'linear-gradient(135deg, #6b4a32, #8b6044)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnGhost: {
    padding: '11px 20px',
    background: 'transparent',
    color: '#8b6044',
    border: '1.5px solid #e8d8c8',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  empty: { color: '#b8a090', fontSize: '14px', padding: '16px 0', textAlign: 'center' },

  roleList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  roleCard: {
    padding: '14px 16px',
    borderRadius: '10px',
    background: '#fdfaf7',
    border: '1.5px solid #f0e4d4',
  },
  roleTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  roleName: { fontSize: '14px', fontWeight: '700', color: '#2d1f14' },
  roleActions: { display: 'flex', gap: '4px' },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    padding: '4px 6px',
    borderRadius: '6px',
    lineHeight: 1,
  },
  permTags: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  permTag: {
    background: '#f0e6d8',
    color: '#7a5030',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
  },
  noPerm: { fontSize: '12px', color: '#c8b0a0', fontStyle: 'italic' },
}

export default AdminRoles
